// src/App.js
import React, { useCallback, useState, useEffect, useRef } from 'react';
import ReactFlow, {
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Controls,
  Background,
  MiniMap,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './App.css';
import jsPDF from 'jspdf';
import { toPng } from 'html-to-image';
import { FaMoon, FaSun, FaDownload, FaSave } from 'react-icons/fa';

// ---------------------------------------------------
// 1. Shape Paths (only 4)
// ---------------------------------------------------
const shapePaths = {
  rect: 'M0,0 H120 V70 H0 Z',
  diamond: 'M60,0 L120,35 L60,70 L0,35 Z',
  triangle: 'M60,0 L120,70 L0,70 Z',
  cloud: 'M30,40 Q20,40 20,30 Q20,20 30,20 Q40,10 55,15 Q60,5 70,15 Q85,10 95,20 Q105,20 105,30 Q105,40 95,40 Q100,50 90,55 Q80,65 65,60 Q50,65 40,55 Q30,50 30,40 Z',
};

// ---------------------------------------------------
// 2. Shape Node – Text on Click
// ---------------------------------------------------
function ShapeNode({ id, data, selected }) {
  const [label, setLabel] = useState(data.label || '');
  const [color, setColor] = useState(data.color || '#3b82f6');
  const [editing, setEditing] = useState(false);

  const update = (key, val) => {
    data[key] = val;
    if (key === 'label') setLabel(val);
    if (key === 'color') setColor(val);
  };

  return (
    <div
      className={`custom-node ${selected ? 'selected' : ''}`}
      onClick={() => setEditing(true)}
    >
      <Handle type="target" position={Position.Top} className="handle" />
      <Handle type="source" position={Position.Bottom} className="handle" />

      <svg width="120" height="70" className="node-svg">
        <path d={shapePaths[data.shape] || shapePaths.rect} fill={color} stroke="#222" strokeWidth="2" />
        {label && (
          <text
            x="60"
            y="38"
            fill="#fff"
            fontSize="13"
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="Inter, system-ui, sans-serif"
            fontWeight="600"
          >
            {label}
          </text>
        )}
      </svg>

      {editing && (
        <input
          className="node-label-input"
          value={label}
          onChange={(e) => update('label', e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => e.key === 'Enter' && setEditing(false)}
          autoFocus
          placeholder="Enter text..."
        />
      )}

      {selected && (
        <input
          type="color"
          className="color-picker"
          value={color}
          onChange={(e) => update('color', e.target.value)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------
// 3. Text Node
// ---------------------------------------------------
function TextNode({ id, data, selected }) {
  const [label, setLabel] = useState(data.label || '');

  return (
    <div className={`text-node ${selected ? 'selected' : ''}`}>
      <Handle type="target" position={Position.Top} className="handle" />
      <Handle type="source" position={Position.Bottom} className="handle" />

      <div className="text-content">
        <textarea
          value={label}
          onChange={(e) => {
            data.label = e.target.value;
            setLabel(e.target.value);
          }}
          className="text-input"
          placeholder="Enter text..."
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {selected && (
        <input
          type="color"
          className="color-picker"
          value={data.color || '#1f2937'}
          onChange={(e) => (data.color = e.target.value)}
        />
      )}
    </div>
  );
}

const nodeTypes = { shape: ShapeNode, text: TextNode };

// ---------------------------------------------------
// 4. Theme Toggle Button
// ---------------------------------------------------
function ThemeToggle({ isDark, toggleTheme }) {
  return (
    <button className="theme-toggle" onClick={toggleTheme} title="Toggle Theme">
      {isDark ? <FaMoon /> : <FaSun />}
    </button>
  );
}

// ---------------------------------------------------
// 5. Toolbar
// ---------------------------------------------------
function Toolbar({ onAddShape, onAddText, isDark, toggleTheme, onExport, onSave }) {
  const shapes = ['rect', 'diamond', 'triangle', 'cloud'];

  return (
    <div className="toolbar">
      {shapes.map((s) => (
        <button key={s} className="action-btn" onClick={() => onAddShape(s)} title={s}>
          <svg width="28" height="28" viewBox="0 0 120 70">
            <path d={shapePaths[s]} fill="#e2e8f0" stroke="#64748b" strokeWidth="2" />
          </svg>
        </button>
      ))}

      <button className="action-btn" onClick={onAddText} title="Add Text">
        T
      </button>

      <div className="toolbar-spacer" />

      {/* Export & Save buttons are now passed as callbacks */}
      <button className="action-btn" onClick={onExport} title="Export PDF">
        <FaDownload />
      </button>

      <button className="action-btn" onClick={onSave} title="Save to localStorage">
        <FaSave />
      </button>

      <ThemeToggle isDark={isDark} toggleTheme={toggleTheme} />
    </div>
  );
}

// ---------------------------------------------------
// 6. PDF Export (shared function)
// ---------------------------------------------------
async function exportToPdf(viewportEl, isDark) {
  if (!viewportEl) return alert('Canvas not ready.');

  // hide controls & minimap while capturing
  document
    .querySelectorAll('.react-flow__controls, .react-flow__minimap')
    .forEach((el) => (el.style.visibility = 'hidden'));

  try {
    const pngDataUrl = await toPng(viewportEl, {
      pixelRatio: 2,
      backgroundColor: isDark ? '#1f2937' : '#f8fafc',
    });

    // restore visibility
    document
      .querySelectorAll('.react-flow__controls, .react-flow__minimap')
      .forEach((el) => (el.style.visibility = ''));

    const img = new Image();
    img.onload = () => {
      const pdf = new jsPDF({
        orientation: img.width > img.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [img.width, img.height],
      });
      pdf.addImage(pngDataUrl, 'PNG', 0, 0, img.width, img.height);
      pdf.save('diagram.pdf');
    };
    img.src = pngDataUrl;
  } catch (err) {
    console.error(err);
    alert('Export failed.');
  }
}

// ---------------------------------------------------
// 7. Main Flow Component
// ---------------------------------------------------
function Flow() {
  const { project } = useReactFlow();
  const viewportRef = useRef(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [isDark, setIsDark] = useState(false);

  // -----------------------------------------------------------------
  // 1. Load saved diagram on mount
  // -----------------------------------------------------------------
  useEffect(() => {
    const saved = localStorage.getItem('reactflow-diagram');
    if (saved) {
      const { nodes: savedNodes, edges: savedEdges } = JSON.parse(saved);
      setNodes(savedNodes);
      setEdges(savedEdges);
    }
  }, []);

  // -----------------------------------------------------------------
  // 2. Theme handling
  // -----------------------------------------------------------------
  useEffect(() => {
    document.body.classList.toggle('dark', isDark);
  }, [isDark]);

  const toggleTheme = () => setIsDark((d) => !d);

  // -----------------------------------------------------------------
  // React Flow callbacks
  // -----------------------------------------------------------------
  const onNodesChange = useCallback((c) => setNodes((n) => applyNodeChanges(c, n)), []);
  const onEdgesChange = useCallback((c) => setEdges((e) => applyEdgeChanges(c, e)), []);

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            animated: true,
            style: { stroke: isDark ? '#94a3b8' : '#555', strokeWidth: 2 },
          },
          eds
        )
      ),
    [isDark]
  );

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Delete' ) {
        setNodes((nds) => nds.filter((n) => !n.selected));
        setEdges((eds) => eds.filter((e) => !e.selected));
      }
    },
    []
  );

  // -----------------------------------------------------------------
  // Add nodes
  // -----------------------------------------------------------------
  const addShape = (shape) => {
    const id = `shape-${Date.now()}`;
    setNodes((nds) => [
      ...nds,
      {
        id,
        type: 'shape',
        position: project({ x: 100 + Math.random() * 300, y: 100 + Math.random() * 200 }),
        data: { label: '', shape, color: '#3b82f6' },
      },
    ]);
  };

  const addText = () => {
    const id = `text-${Date.now()}`;
    setNodes((nds) => [
      ...nds,
      {
        id,
        type: 'text',
        position: project({ x: 100 + Math.random() * 300, y: 100 + Math.random() * 200 }),
        data: { label: '' },
      },
    ]);
  };

  // -----------------------------------------------------------------
  // Export handler (uses the shared export function)
  // -----------------------------------------------------------------
  const handleExport = () => {
    const viewportEl = viewportRef.current;
    exportToPdf(viewportEl, isDark);
  };

  // -----------------------------------------------------------------
  // Save to localStorage
  // -----------------------------------------------------------------
  const handleSave = () => {
    const diagram = JSON.stringify({ nodes, edges });
    localStorage.setItem('reactflow-diagram', diagram);
    alert('Diagram saved locally!');
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  return (
    <div className={`app-container ${isDark ? 'dark' : ''}`}>
      <Toolbar
        onAddShape={addShape}
        onAddText={addText}
        isDark={isDark}
        toggleTheme={toggleTheme}
        onExport={handleExport}
        onSave={handleSave}
      />

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onKeyDown={onKeyDown}
        nodeTypes={nodeTypes}
        fitView
        deleteKeyCode={null}
        // give us a stable ref to the viewport element
        ref={viewportRef}
      >
        <Controls className="flow-controls" />
        <MiniMap nodeColor={(n) => n.data?.color || '#3b82f6'} className="flow-minimap" />

        {/* Dotted Background */}
        <Background
          color={isDark ? '#374151' : '#e2e8f0'}
          gap={20}
          variant="dots"
        />
      </ReactFlow>
    </div>
  );
}

// ---------------------------------------------------
// 8. App
// ---------------------------------------------------
export default function App() {
  return (
    <ReactFlowProvider>
      <Flow />
    </ReactFlowProvider>
  );
}